// Diet plan on the path. Until it is saved or skipped: an unfulfilled card (empty circle, red
// dot until first opened) that starts the questions. Afterwards: a saved plan is a row in the
// streak card; a skipped one is gone from the path and redone from Settings.
import React from 'react';
import { Pressable, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useNav } from '../navigation';
import { useSettings } from '../store/settings';
import { dietPlan, type DietPlan } from '../logic/dietPlan';
import Icon from './Icon';
import { Caption, Row } from './ui';
import { font, radius, spacing, usePalette } from '../theme';

/** Calories, macros and micronutrients of a plan, as a list of rows. */
export function PlanValues({ plan }: { plan: DietPlan }) {
  const { t } = useTranslation();
  const p = usePalette();
  const row = (label: string, value: string, key = label) => (
    <View key={key} style={{ flexDirection: 'row', alignItems: 'baseline', paddingVertical: spacing.s, borderBottomWidth: 0.5, borderBottomColor: p.border, gap: spacing.s }}>
      <Text style={{ flex: 1, fontSize: font.body, color: p.text }}>{label}</Text>
      <Text style={{ fontSize: font.body, fontWeight: '700', color: p.text, fontVariant: ['tabular-nums'] }}>{value}</Text>
    </View>
  );
  return (
    <View style={{ gap: spacing.l }}>
      <View style={{ backgroundColor: p.card, borderRadius: radius.l, paddingHorizontal: spacing.l }}>
        {row(t('nutrition.calories'), `${plan.calories} kcal`)}
        {row(t('nutrition.protein'), `${plan.protein} g`)}
        {row(t('nutrition.fat'), `${plan.fat} g`)}
        {row(t('nutrition.carbohydrates'), `${plan.carbohydrates} g`)}
        {row(t('nutrition.fiber'), `${t('dietPlan.atLeast')} ${plan.fiber} g`)}
        {row(t('nutrition.sugar'), `${t('dietPlan.atMost')} ${plan.sugar} g`)}
        {row(t('dietPlan.water'), `${plan.water} l`)}
      </View>
      <View style={{ gap: spacing.s }}>
        <Text style={{ fontSize: font.headline, fontWeight: '700', color: p.text }}>{t('nutrition.micronutrients')}</Text>
        <View style={{ backgroundColor: p.card, borderRadius: radius.l, paddingHorizontal: spacing.l }}>
          {plan.micros.map(m => row(t(`dietPlan.micros.${m.key}`), `${m.max ? `${t('dietPlan.atMost')} ` : ''}${m.value} ${m.unit}`, m.key))}
        </View>
      </View>
      <View style={{ gap: spacing.s }}>
        <Text style={{ fontSize: font.headline, fontWeight: '700', color: p.text }}>{t('dietPlan.meals')}</Text>
        <View style={{ backgroundColor: p.card, borderRadius: radius.l, paddingHorizontal: spacing.l }}>
          {(['breakfast', 'lunch', 'dinner'] as const).map(slot => row(t(`planner.${slot}`), `${plan.meals[slot]} kcal`, slot))}
        </View>
      </View>
    </View>
  );
}

/**
 * Path card while the diet plan is still open: an empty circle and a red dot until first
 * opened. Gone once a plan is saved (the streak card then carries it, DietPlanStreakRow) or
 * the questions were skipped (redo them from Settings).
 */
export default function DietPlanCard() {
  const { t } = useTranslation();
  const nav = useNav();
  const p = usePalette();
  const done = useSettings(s => s.dietProfile !== null || s.dietPlanSkipped);
  const opened = useSettings(s => s.dietCardOpened);
  const set = useSettings(s => s.set);
  if (done) return null;
  const open = () => {
    if (!opened) set({ dietCardOpened: true });
    nav.navigate('DietPlan');
  };

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={`${t('dietPlan.title')}, ${t('dietPlan.notSetUp')}`}
      onPress={open}
      style={({ pressed }) => ({ backgroundColor: p.card, borderRadius: radius.l * 1.5, padding: spacing.l, gap: spacing.m, opacity: pressed ? 0.75 : 1 })}>
      <Row gap={spacing.m} style={{ flexWrap: 'nowrap' }}>
        <View>
          <View style={{ backgroundColor: p.border, borderRadius: radius.pill, padding: spacing.s }}>
            <Icon name="nutrition" size={28} color={p.secondaryText} />
          </View>
          {!opened ? (
            <View style={{ position: 'absolute', top: -2, right: -2, width: 14, height: 14, borderRadius: 7, backgroundColor: p.danger, borderWidth: 2, borderColor: p.card }} />
          ) : null}
        </View>
        <View style={{ flex: 1 }}>
          <Text style={{ fontSize: font.title3, fontWeight: '800', color: p.text }}>{t('dietPlan.title')}</Text>
          <Caption>{t('dietPlan.cardBody')}</Caption>
        </View>
        <Icon name="circle" color={p.secondaryText} />
      </Row>
    </Pressable>
  );
}

/**
 * The saved diet plan inside the streak card, once the path card is gone: today's calories,
 * macros and pace. After a skip it shows nothing; the questions are redone from Settings.
 */
export function DietPlanStreakRow() {
  const { t } = useTranslation();
  const nav = useNav();
  const p = usePalette();
  const profile = useSettings(s => s.dietProfile);
  if (!profile) return null;
  const plan = dietPlan(profile);
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={`${t('dietPlan.title')}, ${t('dietPlan.perDay', { kcal: plan.calories })}`}
      onPress={() => nav.navigate('DietPlan')}
      style={({ pressed }) => ({ flexDirection: 'row', alignItems: 'center', gap: spacing.m, paddingTop: spacing.m, borderTopWidth: 0.5, borderTopColor: p.border, opacity: pressed ? 0.75 : 1 })}>
      <Icon name="nutrition" size={22} color={p.success} />
      <View style={{ flex: 1, gap: 2 }}>
        <Text style={{ fontSize: font.body, fontWeight: '700', color: p.text }}>{t('dietPlan.perDay', { kcal: plan.calories })}</Text>
        <Caption>
          {`${t('nutrition.protein')} ${plan.protein} g · ${t('nutrition.fat')} ${plan.fat} g · ${t('nutrition.carbs')} ${plan.carbohydrates} g`}
          {plan.goal !== 'keep' ? ` · ${t(`dietPlan.pace_${plan.goal}`, { kg: Math.abs(plan.weeklyChangeKg), count: plan.weeks })}` : ''}
        </Caption>
      </View>
      <Icon name="forward" size={18} color={p.secondaryText} />
    </Pressable>
  );
}
